import {
  Modal,
  ModalContent,
  Spinner,
} from '@heroui/react';
import { AlertTriangle, Archive, Trash2 } from 'lucide-react';
import React, { FC } from 'react';
import { useTranslation } from 'react-i18next';

import {
  CancelButton,
  ConfirmButton,
  Description,
  ModalBodyStyled,
  ModalFooterStyled,
  ModalHeaderStyled,
  ModalTitle,
} from './ModalConfirmation.styled';

interface ModalConfirmationProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (id: string | string[]) => void;
  itemId?: string | string[] | null;
  isLoading?: boolean;
  title?: string;
  description?: string | React.ReactNode;
  buttonConfirm?: string;
}

export const ModalConfirmation: FC<ModalConfirmationProps> = ({
  isOpen,
  onClose,
  onConfirm,
  itemId,
  isLoading,
  title,
  buttonConfirm,
  description,
}) => {
  const { t } = useTranslation();

  const handleConfirm = (): void => {
    if (!itemId) {
      onConfirm('');
    } else {
      onConfirm(itemId);
    }
  };

  const confirmLabel = (buttonConfirm || t('common.delete')).toLowerCase();
  const isArchiveAction = confirmLabel.includes('archiv');
  // ModalConfirmation jest głównie do destrukcyjnych akcji — danger by default.
  const isDangerAction = !isArchiveAction;

  const getIcon = () => {
    if (isLoading) {
      return <Spinner size="sm" color={isDangerAction ? 'danger' : 'white'} />;
    }
    if (isArchiveAction) {
      return <Archive size={14} />;
    }
    if (isDangerAction) {
      return <Trash2 size={14} />;
    }
    return null;
  };

  const getTitleIcon = () => {
    if (isDangerAction) {
      return <AlertTriangle size={20} color="#dc2626" />;
    }
    if (isArchiveAction) {
      return <Archive size={20} color="#111827" />;
    }
    return null;
  };

  return (
    <Modal
      isOpen={isOpen}
      onOpenChange={(open) => {
        if (!open && !isLoading) onClose();
      }}
      backdrop="opaque"
      size="lg"
      classNames={{
        // Ponad drawerami widgetów / szczegółów (z-1100 / z-1200).
        wrapper: 'z-[1300]',
        backdrop: 'z-[1300] bg-black/50 backdrop-blur-sm',
        base: 'bg-white dark:bg-[#1a1f26] rounded-md shadow-2xl border border-gray-200 dark:border-[#374151]',
      }}
    >
      <ModalContent>
        {() => (
          <>
            <ModalHeaderStyled>
              <ModalTitle>
                {getTitleIcon()}
                {title || t('modals.confirmation.deleteTitle')}
              </ModalTitle>
            </ModalHeaderStyled>

            <ModalBodyStyled>
              <Description>
                {description || t('modals.confirmation.deleteWarning')}
              </Description>
            </ModalBodyStyled>

            <ModalFooterStyled>
              <CancelButton
                onPress={onClose}
                isDisabled={!!isLoading}
              >
                {t('common.cancel')}
              </CancelButton>

              <ConfirmButton
                $variant={isDangerAction ? 'danger' : 'primary'}
                onPress={handleConfirm}
                isDisabled={!!isLoading}
                data-testid="modal-confirmation-confirm"
                startContent={getIcon()}
              >
                {buttonConfirm || t('common.delete')}
              </ConfirmButton>
            </ModalFooterStyled>
          </>
        )}
      </ModalContent>
    </Modal>
  );
};
